import { cn } from './cn.js';

/**
 * A marca, escrita uma vez.
 *
 * ## Por que ela precisava de componente
 *
 * "Evogard" aparecia **oito vezes** nas duas SPAs, escrito à mão, em quatro
 * tamanhos (`text-2xl`, `text-lg`, `text-base`, `text-sm`) e com o
 * `font-titulo` e o `tracking-tight` repetidos em cada um. É a peça que menos
 * pode divergir — a decisão 3 do `DESIGN.md` diz que a identidade não se divide
 * por aplicação, porque quem recebe a cobrança precisa reconhecer quem cobra —
 * e era a única sem nenhuma régua.
 *
 * O `PageHeader` já tinha resolvido isso para o título da tela. A marca ficou
 * de fora porque ela **não** é título de tela: ela aparece na moldura navy, na
 * gaveta do telefone, na tela de entrar e na de fora do ar, que são justamente
 * os lugares onde não existe `Page`.
 *
 * ## Dois tamanhos, e o nome deles diz onde ficam
 *
 * - `capa` — a marca é o que a pessoa vê antes de saber onde está: a tela de
 *   entrar, a moldura pública, a de fora do ar. Fundo claro.
 * - `chrome` — dentro do navy: barra lateral, gaveta, cabeçalho do portal.
 *
 * O tamanho e o **fundo** andam juntos de propósito, e é por isso que os nomes
 * são o lugar e não a escala: o subtítulo em `capa` é `texto-suave` e em
 * `chrome` é `chrome-texto-suave`, e essas duas cores nunca se encontram. Um
 * `size="grande"` deixaria a cor do subtítulo de novo por conta de quem escreve
 * a tela, que é o defeito que este arquivo desfaz.
 *
 * A face, o peso e o `tracking` vêm do utilitário `marca` (`theme.css`), e
 * **não** de `font-titulo font-bold tracking-tight` escrito aqui, que era o que
 * havia até 10/09.
 *
 * A diferença não é de organização. O portal aponta `--font-titulo` para
 * Instrument Serif, que tem um peso só — 400 —, e o pedido de `font-bold`
 * chegava ao navegador como negrito SINTETIZADO: a palavra "Evogard" saía com o
 * traço engordado por conta própria, na peça que a decisão 3 do `DESIGN.md`
 * declara ser a que menos pode divergir. Ele já declarava `--font-marca`,
 * `--peso-marca` e `--tracking-marca` para dizer exatamente isso — a marca é o
 * SANS em 600 —, e nenhum dos três era lido por linha nenhuma de código.
 *
 * Declarar a família continua sendo necessário, ao contrário do `PageHeader`: o
 * `theme.css` aplica o token de título em `h1`, `h2` e `h3`, e a marca é `<p>`
 * em seis dos oito lugares — só na tela de entrar ela é o título da página.
 */
/**
 * Em que fundo a marca está — e ele NÃO se deduz do `size`.
 *
 * O subtítulo em `chrome` é `chrome-texto-suave`, que é a cor de apoio sobre a
 * superfície ESCURA. O portal usa a marca em `size="chrome"` dentro da lateral
 * CLARA, e ali aquela cor é a errada: com a paleta laranja do portal,
 * `--cor-chrome-texto-suave` é branco, e "Portal do associado" saía branco
 * sobre branco.
 *
 * Tamanho e fundo eram a mesma pergunta enquanto o navy era o único chrome.
 * Deixaram de ser quando o portal pôs a marca numa lateral clara — e é a mesma
 * separação que `Metric` precisou fazer, pelo mesmo motivo e no mesmo dia.
 */
type Surface = 'chrome' | 'superficie';

/**
 * As props, e a razão de `surface` ser OBRIGATÓRIA quando há subtítulo.
 *
 * Ela tinha padrão `chrome`, e o padrão custou cinco telas. Medido no navegador
 * em 10/09/2026, com a régua de contraste do próprio pacote:
 *
 * | tela                                  | par                       | razão  |
 * | ------------------------------------- | ------------------------- | ------ |
 * | portal: entrar, 1º acesso, senha (×2) | "Área do associado"       | 1,27:1 |
 * | portal: fora do ar                    | "Área do associado"       | 1,27:1 |
 * | interna: entrar e 1º acesso           | "Plataforma"              | 2,33:1 |
 *
 * Nenhuma delas passa nem o piso de 3:1 de elemento gráfico, e a do portal é
 * literalmente invisível. Todas herdavam o padrão sem dizer nada — o mesmo
 * defeito que a prop foi criada para consertar, escondido no valor de fábrica
 * dela. Um padrão que está certo em dois lugares e errado em cinco não é padrão:
 * é uma pergunta não feita.
 *
 * `theme.test.ts` não pega isto e não tem como: ele mede pares de TOKEN que
 * alguém declarou existirem, e o defeito é um componente escolhendo o token
 * errado para o fundo em que foi posto. Quem pega é o tipo — e agora não
 * compila sem a resposta.
 *
 * Sem subtítulo não há o que colorir: a palavra da marca é `currentColor`,
 * herdada de quem a envolve, e aí a prop não faz sentido nenhum.
 */
type WordmarkProps = {
  size?: 'capa' | 'chrome';
  /** `h1` só onde a marca É o título da página: as duas telas de entrar. */
  level?: 'h1' | 'p';
  className?: string;
} & (
  | {
      /** A linha em versalete abaixo: "Plataforma", "Área do associado". */
      subtitle: string;
      surface: Surface;
    }
  | { subtitle?: undefined; surface?: never }
);

export function Wordmark({
  size = 'chrome',
  level = 'p',
  subtitle,
  surface = 'chrome',
  className,
}: WordmarkProps) {
  const Mark = level;
  const capa = size === 'capa';

  return (
    <div className={className}>
      <Mark
        className={cn(
          'marca',
          capa ? 'text-2xl' : 'text-base',
        )}
      >
        Evogard
      </Mark>
      {subtitle && (
        <p
          className={cn(
            'rotulo',
            capa ? 'mt-1' : 'mt-0.5',
            surface === 'superficie' ? 'text-texto-suave' : 'text-chrome-texto-suave',
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}
